import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  hash,
  editorUtils,
  commonUtils,
  useDialogs,
  useNotifications,
  useEditorStore,
  useFlowBuilder2Store,
  useFlowBuilder2DeleteLayoutComponentMutation,
} from '@diana/core';
import { FLOW_BUILDER2_PAGE_NS } from '../i18n';
import { useFlowBuilder2PageStore } from '../Store/useFlowBuilder2PageStore';

export const useDeleteComponent = (
  options?: Parameters<typeof useFlowBuilder2DeleteLayoutComponentMutation>[0],
) => {
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);

  const { confirm } = useDialogs();
  const notifications = useNotifications();

  const getLayoutById = useEditorStore((s) => s.getLayoutById);
  const updateLayoutById = useEditorStore((s) => s.updateLayoutById);
  const registerRequest = useFlowBuilder2Store((s) => s.registerRequest);
  const setActiveComponent = useFlowBuilder2PageStore((s) => s.setActiveComponent);

  const { mutateAsync, isPending: isDeletingComponent } =
    useFlowBuilder2DeleteLayoutComponentMutation({
      ...options,
      onSuccess: (data, variables, context) => {
        options?.onSuccess?.(data, variables, context);
      },
      onError: (error, variables, context) => {
        notifications.error({
          message: t('errors.deleteComponentFailed'),
          description: commonUtils.getErrorMessage(error),
        });
        options?.onError?.(error, variables, context);
      },
    });

  const deleteComponent = useCallback(
    async (args: { layoutId: string; componentId: string; skipConfirm?: boolean }) => {
      const { layoutId, componentId, skipConfirm } = args;

      const layout = getLayoutById(layoutId);
      const component = layout?.components.find((c) => c.id === componentId);

      if (!layout || !component) return false;

      if (!skipConfirm) {
        const { confirmed } = await confirm(null, {
          title: t('prompts.areYouSureToDeleteComponent'),
          variant: 'delete',
        });
        if (!confirmed) return false;
      }

      const descendants = editorUtils.getLayoutComponentDescendants({ componentId, layout });
      const components = [component, ...descendants];
      const componentIds = components.map((c) => c.id);

      setActiveComponent(null);
      updateLayoutById(layoutId, (draft) => {
        draft.components = draft.components.filter((c) => !componentIds.includes(c.id));
        return draft;
      });

      await registerRequest(() => mutateAsync({ layoutId: layout.id, componentId: component.id }), {
        key: hash(['DELETE_LAYOUT_COMPONENT', layout.id, component.id]),
        objectId: layout.id + component.id,
        objectType: 'layoutComponent',
      });

      return true;
    },
    [t, confirm, getLayoutById, updateLayoutById, setActiveComponent, registerRequest, mutateAsync],
  );

  return { deleteComponent, isDeletingComponent };
};
